import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, getBackendUrl, resolveAssetUrl } from '../lib/api';
import SeoHead from '../components/SeoHead';
import RoiCalculator from '../components/landing-suisse/RoiCalculator';
import { trackLanding } from '../lib/landingTrack';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  TrendingDown,
  Users,
  Target,
  Phone,
  Mail,
  Briefcase,
  Headphones,
  Megaphone,
  PenTool,
  Code2,
  HelpCircle,
  Calculator,
  Loader2,
  ChevronRight,
} from 'lucide-react';

/* ========================================================================== */
/* Helpers & data                                                              */
/* ========================================================================== */

const PROFILE_LIBRARY = [
  {
    icon: Briefcase,
    title: 'Secrétaire / Assistante administrative',
    junior: ['Emails et agenda', 'Saisie de documents', 'Devis simples', 'Suivi planning'],
    senior: ['Coordination d’équipe', 'Suivi clients', 'Reporting hebdo', 'Gestion administrative complète'],
  },
  {
    icon: Phone,
    title: 'Prise d’appels & Rendez-vous',
    junior: ['Appels entrants', 'Prise de messages', 'Tenue agenda'],
    senior: ['Qualification de leads', 'Relances structurées', 'Conversion en RDV'],
  },
  {
    icon: Megaphone,
    title: 'Community Manager',
    junior: ['Posts hebdomadaires', 'Réponses commentaires', 'Stories'],
    senior: ['Stratégie éditoriale', 'Contenu de croissance', 'Analytics et A/B'],
  },
  {
    icon: Target,
    title: 'Media Buyer (Meta / Google)',
    junior: ['Campagnes Meta simples', 'Reporting hebdo'],
    senior: ['Scaling ROAS', 'Tunnels complets', 'A/B test continu', 'Multi-canal'],
  },
  {
    icon: Headphones,
    title: 'Support client',
    junior: ['Emails SAV', 'Chat client'],
    senior: ['Process support', 'Satisfaction client', 'Upsell mesuré'],
  },
  {
    icon: Code2,
    title: 'Développeur / Automation',
    junior: ['Landing pages', 'No-code / low-code'],
    senior: ['IA & RAG', 'n8n / Make', 'Intégrations CRM', 'Automatisations business'],
  },
];

/* Pexels public CDN — modern office / business meeting */
const HERO_IMAGE = 'https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1600';

/* ========================================================================== */
/* Sticky CTA bar                                                              */
/* ========================================================================== */

const StickyBar = ({ onCalc, onForm }) => (
  <div
    className="fixed bottom-0 inset-x-0 z-40 border-t border-tp-border bg-white/95 backdrop-blur shadow-[0_-4px_20px_rgba(15,39,68,0.08)]"
    data-testid="sticky-cta-bar"
  >
    <div className="max-w-6xl mx-auto px-3 py-2.5 flex items-center gap-2 sm:gap-3">
      <button
        type="button"
        onClick={() => {
          trackLanding('click_cta_sticky', { meta: { which: 'calc' }, dedupe: false });
          onCalc();
        }}
        className="hidden sm:flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium text-tp-primary border border-tp-border rounded-lg hover:border-tp-primary/40 hover:bg-tp-bg transition-colors"
        data-testid="sticky-calc-btn"
      >
        <Calculator className="h-4 w-4" />
        Calculer mon économie
      </button>
      <Button
        onClick={() => {
          trackLanding('click_cta_sticky', { meta: { which: 'form' }, dedupe: false });
          onForm();
        }}
        className="flex-1 bg-tp-accent hover:bg-tp-accent-light text-white font-semibold py-5 text-sm sm:text-base"
        data-testid="sticky-form-btn"
      >
        Recevoir 3 profils adaptés
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  </div>
);

/* ========================================================================== */
/* Page principale                                                             */
/* ========================================================================== */

const RecruterSuissePage = () => {
  const formRef = useRef(null);
  const calcRef = useRef(null);
  const formStartedTracked = useRef(false);
  const scrollHalfTracked = useRef(false);
  const [logoUrl, setLogoUrl] = useState(null);

  /* page_view + scroll listener */
  useEffect(() => {
    trackLanding('page_view', { meta: { campaign: 'suisse-v1' } });

    const onScroll = () => {
      if (scrollHalfTracked.current) return;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      const ratio = window.scrollY / total;
      if (ratio >= 0.5) {
        scrollHalfTracked.current = true;
        trackLanding('scroll_50');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetch(getBackendUrl() + '/api/branding')
      .then((r) => r.json())
      .then((d) => {
        // Hero a un fond sombre → on privilégie le logo conçu pour fond sombre (blanc/clair).
        if (d.logo_dark_url) setLogoUrl(d.logo_dark_url);
        else if (d.logo_icon_url) setLogoUrl(d.logo_icon_url);
        else if (d.logo_light_url) setLogoUrl(d.logo_light_url);
      })
      .catch(() => {});
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToCalc = () => {
    calcRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ----------------------------- Form state ----------------------------- */
  const [form, setForm] = useState({
    company: '',
    name: '',
    phone: '',
    email: '',
    city: '',
    employees_count: '',
    position_sought: '',
    budget: '',
    urgency: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const setField = (k, v) => {
    if (!formStartedTracked.current) {
      formStartedTracked.current = true;
      trackLanding('start_form');
    }
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const { [k]: _ignored, ...rest } = prev;
      return rest;
    });
  };

  const validate = () => {
    const e = {};
    if (form.company.trim().length < 2) e.company = 'Nom de la société requis';
    if (form.name.trim().length < 2) e.name = 'Votre nom est requis';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Email invalide';
    if (form.phone.trim().length < 6) e.phone = 'Téléphone requis';
    if (form.position_sought.trim().length < 2) e.position_sought = 'Poste à préciser';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      const firstKey = Object.keys(e)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus?.();
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/leads', {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        talent_type: form.position_sought.trim(),
        message: form.message.trim() || null,
        source: 'landing-suisse',
        page: '/recruter-suisse',
        country: 'CH',
        city: form.city.trim() || null,
        employees_count: form.employees_count || null,
        position_sought: form.position_sought.trim(),
        budget: form.budget || null,
        urgency: form.urgency || null,
      });

      if (res.ok) {
        trackLanding('submit_form', {
          meta: { poste: form.position_sought, urgency: form.urgency },
          dedupe: false,
        });

        // Redirection vers la vraie page de remerciement (qui fire la conversion gtag).
        navigate('/merci', {
          state: {
            fromForm: 'suisse_landing',
            name: form.name || '',
          },
        });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Une erreur est survenue. Réessayez.');
      }
    } catch {
      toast.error('Erreur réseau. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-tp-text" data-testid="recruter-suisse-page">
      <SeoHead
        pageTitle="Votre assistante coûte CHF 6'500. Elle pourrait coûter 700. — Talent Partner"
        pageDescription="Optimisez vos coûts de structure tout en gardant l’exigence suisse. Profils francophones sélectionnés, testés et présentés en moins de 15 jours."
        canonical="https://talent-partner-solutions.com/recruter-suisse"
        ogImage="/branding/og-recruter-suisse.jpg"
        ogImageWidth={1200}
        ogImageHeight={900}
      />

      {/* ------------------------------- HEADER ------------------------------- */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={resolveAssetUrl(logoUrl)}
                alt="Talent Partner"
                className="h-9 sm:h-10 object-contain"
                data-testid="suisse-logo"
              />
            ) : (
              <span className="font-display font-semibold text-white text-lg">Talent Partner</span>
            )}
          </Link>
          <a
            href="#form"
            onClick={(e) => {
              e.preventDefault();
              trackLanding('click_cta_primary', { meta: { from: 'header' }, dedupe: false });
              scrollToForm();
            }}
            className="hidden sm:inline-flex items-center gap-1 text-sm text-white/90 hover:text-white"
          >
            Recevoir 3 profils <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      {/* ------------------------------- HERO -------------------------------- */}
      <section className="relative min-h-[88vh] flex items-center pt-24 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt=""
            aria-hidden
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-tp-primary/95 via-tp-primary/85 to-tp-primary/75" />
        </div>

        <div className="relative max-w-6xl mx-auto w-full grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tp-accent/20 border border-tp-accent/40 text-tp-accent-light text-xs font-medium mb-6">
              <Shield className="h-3.5 w-3.5" />
              Pour dirigeants suisses · PME & ETI francophones
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Votre assistante vous coûte CHF&nbsp;6&apos;500.
              <span className="block text-tp-accent-light mt-2">
                Elle pourrait coûter CHF&nbsp;700.
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed">
              Secrétariat, support client, community management, ads, CRM. Talent Partner sélectionne, teste et vous présente <strong className="text-white">2 à 3 profils francophones structurés</strong>, prêts à travailler — alignés sur l&apos;exigence suisse.
            </p>
            <ul className="mt-7 space-y-2.5 text-white/90 text-base">
              {[
                'Profils francophones sélectionnés et testés',
                'Standards de travail alignés sur l’exigence suisse',
                '2 à 3 candidats sérieux à rencontrer (pas 50 CV)',
                'Démarrage en 10 à 15 jours',
              ].map((bullet) => (
                <li key={bullet} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 text-tp-accent-light flex-shrink-0" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  trackLanding('click_cta_primary', { meta: { from: 'hero' }, dedupe: false });
                  scrollToForm();
                }}
                className="bg-tp-accent hover:bg-tp-accent-light text-white font-semibold py-6 px-7 text-base"
                data-testid="hero-cta-primary"
              >
                Recevoir 3 profils adaptés
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  trackLanding('click_calculateur', { meta: { from: 'hero' }, dedupe: false });
                  scrollToCalc();
                }}
                className="bg-white/0 hover:bg-white/10 text-white border-white/40 hover:border-white py-6 px-7 text-base font-medium"
                data-testid="hero-cta-calc"
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calculer mon économie
              </Button>
            </div>
          </div>

          <div className="hidden lg:flex lg:col-span-4 justify-end">
            <div className="bg-white/8 border border-white/15 backdrop-blur-sm rounded-2xl p-6 max-w-sm shadow-xl">
              <div className="text-tp-accent-light text-xs uppercase tracking-wide font-semibold mb-2">
                Cas concret
              </div>
              <p className="text-white/95 text-base leading-relaxed">
                Une assistante administrative facturée localement à <strong>CHF&nbsp;6’500</strong> peut être structurée chez nous pour <strong className="text-tp-accent-light">CHF&nbsp;700</strong>.
              </p>
              <div className="mt-4 pt-4 border-t border-white/15">
                <div className="text-white/60 text-xs">Soit sur 12 mois&nbsp;:</div>
                <div className="text-white font-display text-3xl font-bold tabular-nums">
                  + CHF&nbsp;69’600
                </div>
                <div className="text-white/60 text-xs">de marge récupérée</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- DOULEUR --------------------------- */}
      <section className="py-16 sm:py-20 px-4 bg-tp-bg">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
            Soyons honnêtes.
          </h2>
          <p className="mt-6 text-lg text-tp-text leading-relaxed">
            Le problème n’est pas le manque de candidats. Le problème est&nbsp;:
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              'Vous payez trop cher certaines fonctions support',
              'Vous manquez de temps pour recruter sérieusement',
              'Vous embauchez parfois la mauvaise personne',
              'Vous gardez des tâches qui vous bloquent',
              'Vous freinez votre propre croissance',
            ].map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 bg-white border border-tp-border rounded-lg p-4"
              >
                <XCircle className="h-5 w-5 mt-0.5 text-tp-error flex-shrink-0" />
                <span className="text-tp-text text-sm sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-lg text-tp-primary font-semibold">
            Chaque mois perdu coûte plus qu’un recrutement raté.
          </p>
        </div>
      </section>

      {/* --------------------------- DÉSIR --------------------------- */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
              Imaginez dans 15 jours.
            </h2>
            <p className="mt-4 text-tp-text-light text-lg max-w-2xl mx-auto">
              Un collaborateur francophone motivé, structuré, qui décharge votre quotidien.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Répond aux appels',
              'Gère les emails',
              'Fixe des rendez-vous',
              'Suit vos clients',
              'Met à jour votre CRM',
              'Publie vos réseaux sociaux',
              'Aide vos campagnes Meta / Google',
              'Assiste votre équipe au quotidien',
              'Sans 50 entretiens, sans perdre 3 mois',
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 p-4 rounded-xl border border-tp-border bg-tp-bg/30"
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-tp-accent flex-shrink-0" />
                <span className="text-tp-text">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------- MÉTHODE --------------------------- */}
      <section className="py-16 sm:py-24 px-4 bg-tp-primary text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-semibold uppercase tracking-wider mb-5">
              Notre méthode
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Cinq étapes. Aucun bavardage.
            </h2>
          </div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { n: '01', t: 'Analyse du besoin', d: 'Brief précis. Aucune généralité.' },
              { n: '02', t: 'Sélection ciblée', d: 'Sourcing dans notre vivier francophone qualifié.' },
              { n: '03', t: 'Tests et entretien', d: 'Validation des compétences + capacité remote réelle.' },
              { n: '04', t: '2 à 3 candidats', d: 'Vous ne rencontrez que les meilleurs.' },
              { n: '05', t: 'Cadrage', d: 'Vous choisissez. Nous structurons l’intégration.' },
            ].map((step) => (
              <li
                key={step.n}
                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/8 transition-colors"
              >
                <div className="font-display text-3xl font-bold text-tp-accent-light tabular-nums">
                  {step.n}
                </div>
                <div className="mt-3 font-semibold text-base">{step.t}</div>
                <div className="mt-1.5 text-sm text-white/70 leading-relaxed">{step.d}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* --------------------------- ROI CALCULATOR --------------------------- */}
      <div ref={calcRef}>
        <RoiCalculator onCtaClick={scrollToForm} />
      </div>

      {/* --------------------------- COMPARATIF --------------------------- */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
              Recruter en Suisse vs. via Talent Partner
            </h2>
            <p className="mt-4 text-tp-text-light text-lg">Le vrai comparatif. Sans cosmétique.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border-2 border-tp-border bg-tp-bg/30 p-6 sm:p-8">
              <div className="text-xs uppercase tracking-wider text-tp-text-light font-semibold">
                Employer en Suisse
              </div>
              <h3 className="font-display text-xl font-bold text-tp-primary mt-1.5">
                Le modèle classique
              </h3>
              <div className="mt-5">
                <div className="text-xs uppercase font-semibold text-tp-accent mb-2">Avantages</div>
                <ul className="space-y-1.5 text-sm">
                  {['Présence locale', 'Proximité physique', 'Cadre habituel'].map((x) => (
                    <li key={x} className="flex gap-2">
                      <CheckCircle2 className="h-4 w-4 text-tp-accent mt-0.5 flex-shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5">
                <div className="text-xs uppercase font-semibold text-tp-error mb-2">
                  Inconvénients
                </div>
                <ul className="space-y-1.5 text-sm text-tp-text">
                  {[
                    'Coût total très élevé',
                    'Charges employeur lourdes',
                    'Pénurie sur certains postes',
                    'Recrutement lent (2-4 mois)',
                    'Risque d’erreur coûteux',
                  ].map((x) => (
                    <li key={x} className="flex gap-2">
                      <XCircle className="h-4 w-4 text-tp-error mt-0.5 flex-shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-tp-accent bg-tp-primary text-white p-6 sm:p-8 relative shadow-xl">
              <div className="absolute -top-3 left-6 bg-tp-accent text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                Recommandé
              </div>
              <div className="text-xs uppercase tracking-wider text-tp-accent-light font-semibold">
                Avec Talent Partner
              </div>
              <h3 className="font-display text-xl font-bold mt-1.5">L’approche structurée</h3>
              <div className="mt-5">
                <div className="text-xs uppercase font-semibold text-tp-accent-light mb-2">
                  Avantages
                </div>
                <ul className="space-y-1.5 text-sm">
                  {[
                    'Coûts maîtrisés',
                    'Talents francophones motivés',
                    'Vivier large et qualifié',
                    'Démarrage rapide (10-15 jours)',
                    'Profils filtrés en amont',
                    'Remote natif et organisé',
                  ].map((x) => (
                    <li key={x} className="flex gap-2">
                      <CheckCircle2 className="h-4 w-4 text-tp-accent-light mt-0.5 flex-shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5">
                <div className="text-xs uppercase font-semibold text-white/60 mb-2">
                  Conditions de réussite (nous vous aidons)
                </div>
                <ul className="space-y-1.5 text-sm text-white/80">
                  {[
                    'Management structuré',
                    'Communication digitale claire',
                    'Process documentés',
                  ].map((x) => (
                    <li key={x} className="flex gap-2">
                      <ChevronRight className="h-4 w-4 text-white/50 mt-0.5 flex-shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- PROFILS RECHERCHÉS --------------------------- */}
      <section className="py-16 sm:py-24 px-4 bg-tp-bg">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
              Profils francophones disponibles
            </h2>
            <p className="mt-4 text-tp-text-light text-lg max-w-2xl mx-auto">
              Junior pour structurer. Senior pour piloter. À chaque besoin son niveau.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROFILE_LIBRARY.map((p) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.title}
                  className="bg-white rounded-2xl border border-tp-border p-6 hover:shadow-lg hover:border-tp-accent/40 transition-all"
                  data-testid={`profile-card-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-tp-accent/10 text-tp-accent mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-tp-primary leading-snug">
                    {p.title}
                  </h3>
                  <div className="mt-4 grid grid-cols-2 gap-3 pt-4 border-t border-tp-border">
                    <ProfileLevel label="Junior" items={p.junior} color="text-tp-text-light" />
                    <ProfileLevel label="Senior" items={p.senior} color="text-tp-accent" />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* --------------------------- OBJECTIONS --------------------------- */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tp-primary/10 text-tp-primary text-xs font-semibold uppercase tracking-wider mb-4">
              <HelpCircle className="h-3.5 w-3.5" />
              Vos questions
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
              Trois objections fréquentes. Trois réponses claires.
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: '« Je veux de la qualité suisse. »',
                a: 'Très bien. Gardez la qualité suisse. Déléguez simplement les tâches structurables à des profils performants encadrés intelligemment. Vous gardez votre cœur métier, vous optimisez le reste.',
              },
              {
                q: '« J’ai peur de perdre du temps. »',
                a: 'C’est exactement pour ça que nous filtrons en amont. Vous ne rencontrez que 2 à 3 candidats sérieux. Pas un de plus. Si aucun ne convient, vous payez zéro.',
              },
              {
                q: '« Et si le profil ne convient pas ? »',
                a: 'Le vrai risque, c’est de recruter seul, sans méthode, sous pression. Notre process protège votre temps et votre budget. Garantie de remplacement incluse sur les premiers mois.',
              },
            ].map((obj) => (
              <div
                key={obj.q}
                className="bg-tp-bg/40 border border-tp-border rounded-xl p-5 sm:p-6"
              >
                <div className="font-display text-lg font-semibold text-tp-primary">{obj.q}</div>
                <p className="mt-2.5 text-tp-text leading-relaxed text-base">{obj.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------- URGENCE --------------------------- */}
      <section className="py-14 sm:py-20 px-4 bg-tp-primary text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-tp-accent/20 border border-tp-accent/40 flex items-center justify-center">
              <Clock className="h-6 w-6 text-tp-accent-light" />
            </div>
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                Chaque mois d’attente vous coûte.
              </h2>
              <ul className="mt-5 space-y-2 text-white/85">
                {[
                  'Du temps dirigeant perdu',
                  'Des leads non traités',
                  'Des clients mal suivis',
                  'Des tâches que vous ne devriez plus faire',
                  'Des marges qui diminuent silencieusement',
                ].map((x) => (
                  <li key={x} className="flex gap-2.5 items-start">
                    <TrendingDown className="h-4 w-4 mt-1 text-tp-accent-light flex-shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- FORM --------------------------- */}
      <section
        ref={formRef}
        id="form"
        className="py-16 sm:py-24 px-4 bg-tp-bg"
        data-testid="lead-form-section"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tp-accent/10 text-tp-accent text-xs font-semibold uppercase tracking-wider mb-4">
              <Users className="h-3.5 w-3.5" />
              Demande personnalisée
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
              Recevez vos 3 meilleurs profils
            </h2>
            <p className="mt-4 text-tp-text-light text-lg">
              Réponse sous 24h ouvrées. Sans engagement. Sans CV inutile.
            </p>
          </div>

          <form
              onSubmit={handleSubmit}
              noValidate
              className="bg-white rounded-2xl shadow-lg border border-tp-border p-6 sm:p-8 space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Nom de la société *"
                  name="company"
                  value={form.company}
                  onChange={(v) => setField('company', v)}
                  error={errors.company}
                  testid="form-company"
                />
                <Field
                  label="Votre nom *"
                  name="name"
                  value={form.name}
                  onChange={(v) => setField('name', v)}
                  error={errors.name}
                  testid="form-name"
                />
                <Field
                  label="Email *"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={(v) => setField('email', v)}
                  error={errors.email}
                  testid="form-email"
                />
                <Field
                  label="Téléphone *"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={(v) => setField('phone', v)}
                  error={errors.phone}
                  testid="form-phone"
                />
                <Field
                  label="Ville"
                  name="city"
                  value={form.city}
                  onChange={(v) => setField('city', v)}
                  testid="form-city"
                />
                <SelectField
                  label="Nombre d’employés"
                  name="employees_count"
                  value={form.employees_count}
                  onChange={(v) => setField('employees_count', v)}
                  options={[
                    { v: '', l: '— Sélectionner —' },
                    { v: '1-5', l: '1 à 5' },
                    { v: '6-20', l: '6 à 20' },
                    { v: '21-50', l: '21 à 50' },
                    { v: '51-200', l: '51 à 200' },
                    { v: '200+', l: 'Plus de 200' },
                  ]}
                  testid="form-employees"
                />
                <Field
                  label="Poste recherché *"
                  name="position_sought"
                  value={form.position_sought}
                  onChange={(v) => setField('position_sought', v)}
                  error={errors.position_sought}
                  placeholder="Ex. Assistante administrative"
                  testid="form-position"
                />
                <SelectField
                  label="Budget mensuel estimé"
                  name="budget"
                  value={form.budget}
                  onChange={(v) => setField('budget', v)}
                  options={[
                    { v: '', l: '— Sélectionner —' },
                    { v: '<1000', l: 'Moins de 1’000 CHF' },
                    { v: '1000-2000', l: '1’000 — 2’000 CHF' },
                    { v: '2000-3500', l: '2’000 — 3’500 CHF' },
                    { v: '>3500', l: 'Plus de 3’500 CHF' },
                    { v: 'a-discuter', l: 'À discuter' },
                  ]}
                  testid="form-budget"
                />
              </div>

              <SelectField
                label="Urgence du besoin"
                name="urgency"
                value={form.urgency}
                onChange={(v) => setField('urgency', v)}
                options={[
                  { v: '', l: '— Sélectionner —' },
                  { v: 'immediat', l: 'Immédiat (sous 1 mois)' },
                  { v: '1-3-mois', l: '1 à 3 mois' },
                  { v: 'exploration', l: 'Phase d’exploration' },
                ]}
                testid="form-urgency"
              />

              <div>
                <label htmlFor="form-message" className="text-sm font-medium text-tp-text mb-1.5 block">
                  Message libre (optionnel)
                </label>
                <textarea
                  id="form-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setField('message', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-tp-border focus:outline-none focus:ring-2 focus:ring-tp-accent text-base"
                  placeholder="Précisez votre contexte, vos contraintes…"
                  data-testid="form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-tp-accent hover:bg-tp-accent-light text-white font-semibold py-6 text-base"
                data-testid="form-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi…
                  </>
                ) : (
                  <>
                    Recevoir 3 profils adaptés <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
              <p className="text-xs text-tp-text-light text-center pt-2">
                Un assistant vous rappelle rapidement pour qualifier votre besoin et vous proposer un créneau avec un conseiller.
              </p>
            </form>
        </div>
      </section>

      {/* --------------------------- FINAL --------------------------- */}
      <section className="py-16 sm:py-20 px-4 bg-tp-primary text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Vous avez deux choix.
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="text-white/60 text-xs uppercase tracking-wider font-semibold">
                Option 1
              </div>
              <p className="mt-2 text-white/85">
                Continuer à payer trop cher, rester débordé, reporter encore.
              </p>
            </div>
            <div className="bg-tp-accent/20 border border-tp-accent/40 rounded-xl p-5">
              <div className="text-tp-accent-light text-xs uppercase tracking-wider font-semibold">
                Option 2
              </div>
              <p className="mt-2 text-white">
                Structurer une équipe rentable avec des talents francophones qualifiés.
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              trackLanding('click_cta_primary', { meta: { from: 'final' }, dedupe: false });
              scrollToForm();
            }}
            className="mt-9 bg-white text-tp-primary hover:bg-white/90 font-semibold py-6 px-6 sm:px-8 text-base max-w-full whitespace-normal h-auto"
            data-testid="final-cta"
          >
            Recevoir mes profils maintenant
            <ArrowRight className="h-4 w-4 ml-2 shrink-0" />
          </Button>
        </div>
      </section>

      {/* --------------------------- FOOTER --------------------------- */}
      <footer className="py-8 px-4 bg-tp-primary text-white/70 text-center text-sm border-t border-white/10">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>© {new Date().getFullYear()} Talent Partner — Tous droits réservés</div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
            <Link to="/cgv" className="py-2 px-1 hover:text-white">
              CGV
            </Link>
            <Link to="/mentions-legales" className="py-2 px-1 hover:text-white">
              Mentions légales
            </Link>
            <a
              href="mailto:info@talent-partner-solutions.com"
              className="inline-flex items-center gap-1 py-2 px-1 hover:text-white"
            >
              <Mail className="h-3 w-3" /> Contact
            </a>
          </div>
        </div>
      </footer>

      {/* --------------------------- STICKY CTA --------------------------- */}
      <div className="pb-20" />
      <StickyBar onCalc={scrollToCalc} onForm={scrollToForm} />
    </div>
  );
};

const ProfileLevel = ({ label, items, color }) => (
  <div>
    <div className={`text-[11px] uppercase tracking-wide font-semibold ${color} mb-1.5`}>
      {label}
    </div>
    <ul className="space-y-1 text-[12.5px] text-tp-text-light leading-snug">
      {items.map((x) => (
        <li key={x}>· {x}</li>
      ))}
    </ul>
  </div>
);

const Field = ({ label, value, onChange, error, type = 'text', name, placeholder, testid }) => (
  <div>
    <label htmlFor={name} className="text-sm font-medium text-tp-text mb-1.5 block">
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-tp-accent text-base ${
        error ? 'border-tp-error bg-red-50/40' : 'border-tp-border'
      }`}
      data-testid={testid}
    />
    {error && (
      <p className="text-xs text-tp-error mt-1" data-testid={`${testid}-error`}>
        {error}
      </p>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, name, testid }) => (
  <div>
    <label htmlFor={name} className="text-sm font-medium text-tp-text mb-1.5 block">
      {label}
    </label>
    <select
      id={name}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-lg border border-tp-border bg-white focus:outline-none focus:ring-2 focus:ring-tp-accent text-base"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.l}
        </option>
      ))}
    </select>
  </div>
);

export default RecruterSuissePage;
